/**
 * Privacy-preserving page-view tracker.
 *
 * Fires one event per route change to /api/_analytics/event. The backend
 * looks up the IP → country/state/city and immediately discards the IP.
 * Visitors are identified ONLY by an anonymous UUID cookie that lives in
 * localStorage; no login, no email, no personal field.
 *
 * Usage: drop <Analytics /> inside the BrowserRouter, beneath ScrollToTop.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANON_KEY = "rm_anon_id";

function getAnonId() {
  if (typeof window === "undefined") return null;
  try {
    let id = window.localStorage.getItem(ANON_KEY);
    if (!id) {
      // crypto.randomUUID is available in every browser we support; fall
      // back to a Math.random hash for paranoid environments.
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `rm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

// Map pathname → engine bucket. Keeps the admin dashboard categories
// tight and stable even if route paths are tweaked later.
function classifyEngine(pathname) {
  const p = (pathname || "/").toLowerCase();
  if (p === "/" || p === "") return { engine: "landing", page: "/" };
  if (p.startsWith("/intake") || p.startsWith("/generating") || p.startsWith("/packet"))
    return { engine: "matrix", page: p.split("/").slice(0, 2).join("/") || "/intake" };
  if (p.startsWith("/discernment")) return { engine: "warfare", page: "/discernment" };
  if (p.startsWith("/progress")) return { engine: "progress", page: "/progress" };
  if (p.startsWith("/chat")) return { engine: "chat", page: "/chat" };
  if (p.startsWith("/library")) return { engine: "library", page: "/library" };
  if (p.startsWith("/community")) return { engine: "community", page: "/community" };
  if (p.startsWith("/donate") || p.startsWith("/donation"))
    return { engine: "donate", page: "/donate" };
  if (p.startsWith("/admin")) return { engine: "admin", page: "/admin" };
  return { engine: "other", page: p.slice(0, 64) };
}

export default function Analytics() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!BACKEND_URL) return;
    const anonId = getAnonId();
    if (!anonId) return;
    const { engine, page } = classifyEngine(pathname);
    // Fire-and-forget. Errors are intentionally swallowed — analytics
    // must NEVER break the user experience.
    axios
      .post(
        `${BACKEND_URL}/api/_analytics/event`,
        { anon_id: anonId, page, engine },
        { timeout: 4000 },
      )
      .catch(() => {});
  }, [pathname]);

  return null;
}
